import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, Briefcase, BriefcaseBusiness, Building2, CalendarClock, CalendarDays, ClipboardList, ContactRound, Database, FileText, FileUp, Forward, GitMerge, Inbox, LayoutDashboard,
  ListChecks, ListTodo, LogOut, Menu, PhoneMissed, ReceiptText, Search, Settings, UserCog, UserRoundCheck, Users, UserSquare2,
} from "lucide-react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { UniversalSearch } from "@/components/search/UniversalSearch";
import { NotificationBell } from "@/components/common/NotificationBell";
import { api } from "@/lib/api";

const NAV = {
  member: [
    { to: "/member", label: "My Applications", icon: ClipboardList, end: true },
    { to: "/member/resume", label: "Resume Builder", icon: FileText },
  ],
  recruiter: [
    { to: "/recruiter", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/recruiter/unassigned", label: "Fresh Leads", icon: Inbox },
    { to: "/recruiter/applications", label: "My Applications", icon: ClipboardList },
    { to: "/recruiter/scheduled", label: "Scheduled", icon: CalendarClock },
    { to: "/recruiter/callbacks", label: "Call Backs", icon: PhoneMissed },
    { to: "/recruiter/lineup-board", label: "Going", icon: CalendarDays },
    { to: "/recruiter/selection", label: "Selection", icon: ListChecks },
    { to: "/recruiter/joined", label: "Joined", icon: UserRoundCheck },
    { to: "/recruiter/employment", label: "Joining Confirmation", icon: BriefcaseBusiness },
    { to: "/recruiter/candidates", label: "Candidate Directory", icon: ContactRound },
    { to: "/recruiter/companies", label: "Companies", icon: Building2 },
    { to: "/recruiter/forward-leads", label: "Forward Leads", icon: Forward },
    { to: "/notifications", label: "Notifications", icon: Bell },
    { to: "/recruiter/profile", label: "My Profile", icon: UserSquare2 },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/fresh-leads", label: "Fresh Leads", icon: Inbox },
    { to: "/admin/work-queue", label: "Work Queue", icon: ListTodo },
    { to: "/admin/lineup-board", label: "Daily Lineup", icon: CalendarDays },
    { to: "/admin/scheduled", label: "Scheduled", icon: CalendarClock },
    { to: "/admin/selection", label: "Selection", icon: ListChecks },
    { to: "/notifications", label: "Notifications", icon: Bell },
    { to: "/admin/members", label: "Members", icon: UserSquare2 },
    { to: "/admin/candidates", label: "Candidates", icon: ContactRound },
    { to: "/admin/companies", label: "Companies", icon: Building2 },
    { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
    { to: "/admin/applications", label: "Applications", icon: ClipboardList },
    { to: "/admin/employment", label: "Employment", icon: BriefcaseBusiness },
    { to: "/admin/invoice-pending", label: "Invoice Pending", icon: ReceiptText },
    { to: "/admin/followups", label: "Follow-up Management", icon: CalendarClock },
    { to: "/admin/lineups", label: "Lineup Management", icon: ListChecks },
    { to: "/admin/interviews", label: "Interview Management", icon: CalendarDays },
    { to: "/admin/forward-leads", label: "Forward Leads", icon: Forward },
    { to: "/admin/allocations", label: "Recruiter Allocation", icon: UserCog },
    { to: "/admin/recruiters", label: "Recruiters", icon: Users },
    { to: "/admin/upload-data", label: "Upload Data", icon: FileUp },
    { to: "/admin/disputes", label: "Lead Disputes", icon: AlertTriangle },
    { to: "/admin/master-data", label: "Master Data", icon: Database },
    { to: "/admin/profile-conflicts", label: "Profile Conflicts", icon: GitMerge },
    { to: "/admin/settings", label: "Admin Settings", icon: Settings },
    { to: "/admin/activity-logs", label: "Activity Logs", icon: Activity },
  ],
};

const RoleLabel = { member: "Member Portal", recruiter: "Recruiter Portal", admin: "Admin Portal", super_admin: "Admin Portal" };

const navForRole = (role) => (role === "super_admin" ? NAV.admin : NAV[role] || []);

const SidebarContent = ({ role, pathname, onNavigate }) => {
  const [cbOverdue, setCbOverdue] = useState(0);
  useEffect(() => {
    if (role !== "recruiter") return;
    let alive = true;
    const fetchCount = () => api.get("/recruiter-flow/callbacks/count")
      .then((r) => { if (alive) setCbOverdue(r.data.overdue || 0); })
      .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [role, pathname]);

  return (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-2 px-5 h-16 border-b border-border" data-testid="sidebar-logo">
        <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
          <Briefcase className="h-4 w-4 text-white" />
        </span>
        <div className="leading-tight">
          <p className="font-heading font-semibold text-sm">Udhyog<span className="text-primary">Tech</span></p>
          <p className="text-[11px] text-muted-foreground">{RoleLabel[role]}</p>
        </div>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navForRole(role).map((item) => {
          const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
          const Icon = item.icon;
          const badge = item.to === "/recruiter/callbacks" && cbOverdue > 0 ? cbOverdue : null;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {badge && (
                <span data-testid="sidebar-callbacks-overdue-badge"
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold">
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export const PortalShell = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-border z-30">
        <SidebarContent role={user.role} pathname={location.pathname} />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 bg-white/80 backdrop-blur-md border-b border-border flex items-center gap-3 px-4 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent role={user.role} pathname={location.pathname} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <button
            onClick={() => setSearchOpen(true)}
            data-testid="topbar-search-trigger"
            className="flex items-center gap-2 flex-1 min-w-0 max-w-md rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left truncate">Search mobile, name, application ID, job...</span>
            <kbd className="hidden sm:inline-flex font-mono text-[10px] bg-white border border-border rounded px-1.5 py-0.5">Ctrl K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-1">
            {user.role !== "member" && <NotificationBell />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 rounded-lg" data-testid="topbar-user-menu">
                  <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                    {(user.name || "U").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden sm:block text-sm font-medium">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{user.mobile}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/")}>Public website</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>

      <UniversalSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
};
